import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import {
  useGetBusinessQuery,
  useGetPaymentGatewaysQuery,
  useUpdateStripeGatewayMutation,
} from "../../store/api";

const PaymentGateways = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const { data: business } = useGetBusinessQuery(businessId);
  const {
    data: gateways,
    isLoading,
    isError,
    error,
  } = useGetPaymentGatewaysQuery(businessId);
  const [updateStripe, updateState] = useUpdateStripeGatewayMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    isEnabled: false,
    isDefault: false,
    publishable_key: "",
    secret_key: "",
    webhook_secret: "",
  });

  useEffect(() => {
    if (gateways && Array.isArray(gateways)) {
      const stripe = gateways.find(
        (g) => g.provider === "STRIPE" || g.provider === "Stripe",
      );
      if (stripe) {
        setForm({
          isEnabled: stripe.isEnabled || false,
          isDefault: stripe.isDefault || false,
          publishable_key: stripe.config?.publishable_key || "",
          secret_key: stripe.config?.secret_key || "",
          webhook_secret: stripe.config?.webhook_secret || "",
        });
      }
    }
  }, [gateways]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        isEnabled: form.isEnabled,
        isDefault: form.isDefault,
        config: {
          publishable_key: form.publishable_key,
          secret_key: form.secret_key,
          webhook_secret: form.webhook_secret,
        },
      };
      await updateStripe({ businessId, payload }).unwrap();
      setIsEditing(false);
    } catch (err) {
      // error handled by UI
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Payment Gateways" subtitle="Loading..." />
        <Card>
          <p className="text-sm text-slate-500">Loading payment gateways...</p>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Payment Gateways"
          subtitle="Error loading gateways"
        />
        <Card>
          <Alert tone="error">
            {error?.data?.detail || "Failed to load payment gateways."}
          </Alert>
        </Card>
      </div>
    );
  }

  const stripeGateway = gateways?.find(
    (g) => g.provider === "STRIPE" || g.provider === "Stripe",
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Gateways"
        subtitle={`${business?.businessName || ""} • Configure payment processing`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        }
      />

      {/* Stripe Configuration */}
      <Card>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Stripe</h2>
              <p className="text-sm text-slate-600">
                Accept credit card payments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {stripeGateway?.isEnabled && <Badge tone="success">Enabled</Badge>}
            {stripeGateway?.isDefault && <Badge tone="info">Default</Badge>}
            {!isEditing && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditing(true)}
              >
                Configure
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Alert tone="info">
              <strong>Security Note:</strong> API keys are masked in the
              response for security. Only enter new values if you want to update
              them.
            </Alert>

            <div className="space-y-4">
              <Checkbox
                name="isEnabled"
                checked={form.isEnabled}
                onChange={handleChange}
                label="Enable Stripe"
                helper="Allow this business to accept payments via Stripe"
              />

              <Checkbox
                name="isDefault"
                checked={form.isDefault}
                onChange={handleChange}
                label="Set as Default Gateway"
                helper="Use Stripe as the primary payment processor"
              />
            </div>

            <div className="space-y-4">
              <Input
                label="Publishable Key"
                name="publishable_key"
                value={form.publishable_key}
                onChange={handleChange}
                placeholder="pk_live_..."
                helper="Your Stripe publishable key (starts with pk_)"
                required
              />

              <Input
                label="Secret Key"
                name="secret_key"
                type="password"
                value={form.secret_key}
                onChange={handleChange}
                placeholder="sk_live_..."
                helper="Your Stripe secret key (starts with sk_) - kept secure"
                required
              />

              <Input
                label="Webhook Secret"
                name="webhook_secret"
                type="password"
                value={form.webhook_secret}
                onChange={handleChange}
                placeholder="whsec_..."
                helper="Your Stripe webhook signing secret (starts with whsec_)"
                required
              />
            </div>

            {updateState.isSuccess && (
              <Alert tone="success">
                Stripe configuration updated successfully.
              </Alert>
            )}

            {updateState.isError && (
              <Alert tone="error">
                {updateState.error?.data?.detail ||
                  "Failed to update Stripe configuration."}
              </Alert>
            )}

            <div className="flex gap-3 border-t border-slate-200 pt-6">
              <Button type="submit" disabled={updateState.isLoading}>
                {updateState.isLoading ? "Saving..." : "Save Configuration"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                  if (stripeGateway) {
                    setForm({
                      isEnabled: stripeGateway.isEnabled || false,
                      isDefault: stripeGateway.isDefault || false,
                      publishable_key:
                        stripeGateway.config?.publishable_key || "",
                      secret_key: stripeGateway.config?.secret_key || "",
                      webhook_secret:
                        stripeGateway.config?.webhook_secret || "",
                    });
                  }
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-slate-600">Status</dt>
                <dd className="mt-1">
                  {stripeGateway?.isEnabled ? (
                    <Badge tone="success">Enabled</Badge>
                  ) : (
                    <Badge tone="neutral">Disabled</Badge>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-600">
                  Default Gateway
                </dt>
                <dd className="mt-1">
                  {stripeGateway?.isDefault ? (
                    <Badge tone="info">Yes</Badge>
                  ) : (
                    <span className="text-sm text-slate-500">No</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-600">
                  Publishable Key
                </dt>
                <dd className="mt-1 font-mono text-sm text-slate-900">
                  {stripeGateway?.config?.publishable_key || (
                    <span className="text-slate-400">Not configured</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-600">
                  Secret Key
                </dt>
                <dd className="mt-1 font-mono text-sm text-slate-900">
                  {stripeGateway?.config?.secret_key ? (
                    <span className="text-slate-400">••••••••••••••••</span>
                  ) : (
                    <span className="text-slate-400">Not configured</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-slate-600">
                  Webhook Secret
                </dt>
                <dd className="mt-1 font-mono text-sm text-slate-900">
                  {stripeGateway?.config?.webhook_secret ? (
                    <span className="text-slate-400">••••••••••••••••</span>
                  ) : (
                    <span className="text-slate-400">Not configured</span>
                  )}
                </dd>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Future Gateways Placeholder */}
      <Card>
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-slate-900">
            More Gateways Coming Soon
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Additional payment gateways will be available in future updates.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PaymentGateways;
